import { Action } from '@ngrx/store';
import { forkJoin, Observable, of, OperatorFunction, PartialObserver, race, Subject } from 'rxjs';
import { filter, first, map, switchMap, take } from 'rxjs/operators';
import { Actions } from '@ngrx/effects';
import { ActionWithCorrelationAttributes } from './correlation-actions';

export const AggregationActionType = '[NGRX-HELPER] Aggregation Action';

/**
 * A Queue that provides each subscriber all values emitted from the beginning of the lifetime of this queue
 */
class Queue<T> extends Subject<T> {

  private items: T[] = [];
  private enabled = false;

  enable() {
    this.enabled = true;
    setTimeout(
      () => {
        this.items = [];
      },
      1
    );
  }

  next(value: T) {
    this.enabled
      ? super.next(value)
      : this.items.push(value);
  }

  public subscribe (
    nextOrObserver: ((value: T) => void) | PartialObserver<T>,
    error?:         (error: any) => void,
    complete?:      () => void,
  ) {
    const subscription = (typeof nextOrObserver === 'function')
      ? super.subscribe(nextOrObserver, error, complete)
      : super.subscribe(nextOrObserver);
    this.items.forEach(item => super.next(item));
    return subscription;
  }
}

export class AggregationAction implements Action {
  readonly type = AggregationActionType;
  constructor(
    public payload: {
      callback?: () => (ActionWithCorrelationAttributes)[]
      successAction: Action;
      errorAction: Action;
      waitFor?: string[],
      failOn?: string[]
    },
    public correlationId: string
  ) { }
}

export function aggregate(
  actionSource$: Actions
): OperatorFunction<Action, Action> {
  const filterAction = (
    action: ActionWithCorrelationAttributes,
    actionType,
    correlationId
  ): boolean => {
    return action.type === actionType &&
      action.__correlationId === correlationId;
  };

  const getResults = (sourceAction: AggregationAction): Observable<Action> => {
    const queue = new Queue<Action>();
    const actions$ = queue.asObservable();

    // filter all actions that have no aggregation params and forward them into the queue
    actionSource$.pipe(
      filter(action => action['__correlationId']),
    ).subscribe(action => queue.next(action));

    const waitFor = sourceAction.payload.waitFor ? sourceAction.payload.waitFor : [];
    const failOn = sourceAction.payload.failOn ? sourceAction.payload.failOn : [];
    if (sourceAction.payload.callback) {
      const ActionWithCorrelationAttributess = sourceAction.payload.callback();
      ActionWithCorrelationAttributess.forEach((action) => {
        waitFor.push(action.__successActionType);
        failOn.push(action.__errorActionType);
      });
      // TODO if not callback or waitFor/failOn contains empty action types  "" => emit success instantly
    }

    const successActions$: Observable<Action>[] = waitFor.map(actionType =>
      actions$.pipe(
        filter((a: ActionWithCorrelationAttributes) =>
          filterAction(
            a,
            actionType,
            sourceAction.correlationId
          )
        ),
        first()
      )
    );

    /**
     * successActions$ would never fire if it would be empty
     */
    if (successActions$.length === 0) {
      return of(sourceAction.payload.successAction);
    }

    const success$ = forkJoin(successActions$).pipe(
      map(() => sourceAction.payload.successAction),
    );

    const error$ = actions$.pipe(
      filter((action: ActionWithCorrelationAttributes) =>
        failOn.find((errorActionType) => filterAction(
            action,
            errorActionType,
            sourceAction.correlationId
          )
        ) !== undefined
      ),
      map(() => sourceAction.payload.errorAction),
      first()
    );

    queue.enable();

    return race(success$, error$);
  };

  return (source: Observable<AggregationAction>) => {
    return source.pipe(
      switchMap(sourceAction => {
        return getResults(sourceAction);
      }),
      take(1)
    );
  }
}
